import React, { useCallback, useEffect } from "react";

import cn from "classnames";

import { AnyEntity } from "@mapmycustomers/shared/types/entity";
import { isDefined, isNotEmpty } from "@mapmycustomers/shared/util/assert";
import useRerenderer from "@mapmycustomers/shared/util/hook/useRerenderer";

import { useAgGridListView } from "../../utils/useListView";

import styles from "./AgGridListViewPagination.module.scss";
import Pagination from "./Pagination";

const getFirstVisibleRowIndex = (): number | undefined => {
  // AgGrid doesn't have a built-in method to get first visible row
  // Methods like getFirstRenderedRow/getFirstDisplayedRow return incorrect results because they count buffer too.
  // Same for gridApi.getModel().getRowIndexAtPixel(0).
  // So we have to use DOM elements to find an _actually_ displayed rows.
  const bodyRect = document.querySelector(".ag-body-viewport")?.getBoundingClientRect();
  if (bodyRect) {
    // 1 here is used to move a bit inside body's viewport
    const firstVisible = document.elementFromPoint(bodyRect.x + 1, bodyRect.top + 1);
    if (firstVisible) {
      const rowIndex = firstVisible.closest(".ag-row")?.getAttribute("row-index");
      return isNotEmpty(rowIndex) ? parseInt(rowIndex, 10) : undefined;
    }
  }
  return undefined;
};

interface Props {
  allowedPageSizes?: number[];
  className?: string;
  showSizeChanger?: boolean;
  totalRecords: number;
}

const AgGridListViewPagination = <T extends AnyEntity>({
  allowedPageSizes,
  className,
  showSizeChanger,
  totalRecords,
}: Props) => {
  const { gridApi } = useAgGridListView<T>();
  const handlePageChange = useCallback(
    (page: number) => {
      gridApi?.paginationGoToPage(page - 1);
    },
    [gridApi]
  );

  // This code is needed to correctly catch any pagination changes made by AgGrid itself.
  // Normally, this shouldn't happen. But it's still better to listen to any changes and
  // reflect them in the Pagination component.
  const forceRender = useRerenderer();
  useEffect(() => {
    const listener = () => forceRender();
    gridApi?.addEventListener("paginationChanged", listener);
    return () => {
      gridApi?.removeEventListener("paginationChanged", listener);
    };
  }, [gridApi, forceRender]);

  const handlePageSizeChange = useCallback(
    (newPageSize: number) => {
      if (!gridApi) {
        return;
      }
      const firstVisibleRowIndex = getFirstVisibleRowIndex();
      // we need to change page size so that the paginationGetPageSize returns
      // new value and that value is shown in size selectbox
      gridApi.paginationSetPageSize(newPageSize);

      // gridOptionsWrapper is a private field, so we shouldn't normally use it,
      // but unfortunately AgGrid left no other way to change cache block size.
      // I checked a ton of resources and none had a reliable way of doing that.
      // Moreover, I tried changing cacheBlockSize property on React's AgGrid
      // component, but with no effect. I could probably also use
      // (gridApi.getModel() as ServerSideModel).setCacheBlockSize,
      // but this method is private too.
      //
      // We have to keep page size and block size equal, so that we fully load
      //  page's data. Otherwise, page is loaded in chunks and if user tries
      // to use "Select All" checkbox, that will only select loaded records
      // instead of selecting all page's records.
      // @ts-ignore
      gridApi.gridOptionsWrapper.setProperty("cacheBlockSize", newPageSize);

      // implicitly reset cache, otherwise new cacheBlockSize is not used
      gridApi.onFilterChanged();

      // Now, after page size was changed, we are going to the same record
      // we saw before changing size, to keep user position in the grid.
      // This is not required, and unfortunately generates a little glitch
      // with double page loading, but it feels a correct thing to do.
      // Otherwise, we'd just switch to the first page.
      // Also, we can't call ensureIndexVisible immediately after resetting
      // cache block size, as it won't work. Hence, we do that on
      // modelUpdated event.
      if (isDefined(firstVisibleRowIndex)) {
        const listener = () => {
          gridApi.removeEventListener("modelUpdated", listener);
          // let's get back to that row again
          gridApi.ensureIndexVisible(firstVisibleRowIndex, "top");
        };
        gridApi.addEventListener("modelUpdated", listener);
      }
    },
    [gridApi]
  );

  return (
    <Pagination
      allowedPageSizes={allowedPageSizes}
      className={cn(styles.container, className)}
      currentPage={(gridApi?.paginationGetCurrentPage() ?? 0) + 1}
      onChange={handlePageChange}
      onPageSizeChange={showSizeChanger ? handlePageSizeChange : undefined}
      pageSize={gridApi?.paginationGetPageSize()}
      totalRecords={totalRecords}
    />
  );
};

export default AgGridListViewPagination;
